import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Check, Search, ChevronDown } from 'lucide-react';
import { platformAccountService } from '@/services/platformAccountService';
import useGlobalCustomerStore from '@/stores/globalCustomerStore';
import { clsx } from 'clsx';

interface Account {
  id: string;
  username: string;
  display_name?: string | null;
  platform_id: number;
  platform_name?: string;
}

interface AccountPickerProps {
  selectedAccountIds: string[];
  onChange: (accountIds: string[]) => void;
  customerId?: string;
  error?: string;
  disabled?: boolean;
  required?: boolean;
}

// Platform IDs match backend: 1=Instagram, 2=TikTok, 3=Facebook, 4=Twitter, 5=YouTube
const PLATFORM_NAMES: Record<number, string> = {
  1: 'Instagram',
  2: 'TikTok',
  3: 'Facebook',
  4: 'X (Twitter)',
  5: 'YouTube',
};

const PLATFORM_COLORS: Record<number, { bg: string; text: string }> = {
  1: { bg: 'bg-pink-100', text: 'text-pink-700' },  // Instagram
  2: { bg: 'bg-black', text: 'text-white' },         // TikTok
  3: { bg: 'bg-blue-100', text: 'text-blue-700' },   // Facebook
  4: { bg: 'bg-gray-100', text: 'text-gray-700' },   // Twitter/X
  5: { bg: 'bg-red-100', text: 'text-red-700' },     // YouTube
};

const AccountPicker: React.FC<AccountPickerProps> = ({
  selectedAccountIds,
  onChange,
  customerId: propCustomerId,
  error,
  disabled = false,
  required = false,
}) => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { selectedCustomer } = useGlobalCustomerStore();

  // Use prop customerId if provided, otherwise fall back to global store
  const effectiveCustomerId = propCustomerId || selectedCustomer?.id;

  // Update dropdown position when opened or on scroll/resize
  useEffect(() => {
    if (!isOpen || !buttonRef.current) return;

    const updatePosition = () => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + 4,
          left: rect.left,
          width: rect.width,
        });
      }
    };

    updatePosition();

    // Update position on scroll or resize
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  useEffect(() => {
    const loadAccounts = async () => {
      if (!effectiveCustomerId) return;

      setIsLoading(true);
      try {
        const response = await platformAccountService.getCustomerPlatformAccounts(
          String(effectiveCustomerId),
          { limit: 100 }
        );
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const responseData = response.data as any;
        // Backend returns 'accounts', but type expects 'platformAccounts' - handle both
        const accountData = responseData?.platformAccounts || responseData?.accounts || responseData || [];

        if (!Array.isArray(accountData)) {
          console.error('AccountPicker: Invalid accounts data:', accountData);
          setAccounts([]);
          return;
        }

        setAccounts(
          accountData.map((acc: any) => ({
            id: acc.id,
            username: acc.username,
            display_name: acc.displayName || acc.display_name,
            platform_id: acc.platformId || acc.platform_id,
            platform_name: PLATFORM_NAMES[acc.platformId || acc.platform_id] || 'Unknown',
          }))
        );
      } catch (err) {
        console.error('Failed to load accounts:', err);
        setAccounts([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadAccounts();
  }, [effectiveCustomerId]);

  const filteredAccounts = accounts.filter(
    (account) =>
      account.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (account.display_name && account.display_name.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const toggleAccount = (accountId: string) => {
    if (selectedAccountIds.includes(accountId)) {
      onChange(selectedAccountIds.filter((id) => id !== accountId));
    } else {
      onChange([...selectedAccountIds, accountId]);
    }
  };

  const selectedCount = selectedAccountIds.length;

  // Render dropdown in a portal to escape overflow containers
  const dropdownContent = isOpen && createPortal(
    <>
      {/* Click outside to close */}
      <div
        className="fixed inset-0 z-[9998]"
        onClick={() => setIsOpen(false)}
      />

      {/* Dropdown Menu */}
      <div
        className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-hidden"
        style={{
          top: dropdownPosition.top,
          left: dropdownPosition.left,
          width: dropdownPosition.width,
        }}
      >
        {/* Search Input */}
        <div className="p-2 border-b border-gray-100">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search accounts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Account List */}
        <div className="overflow-y-auto max-h-48">
          {isLoading ? (
            <div className="p-4 text-center text-xs text-gray-500">Loading accounts...</div>
          ) : filteredAccounts.length === 0 ? (
            <div className="p-4 text-center text-xs text-gray-500">
              {searchQuery ? 'No accounts match your search' : 'No accounts available'}
            </div>
          ) : (
            filteredAccounts.map((account) => {
              const isSelected = selectedAccountIds.includes(account.id);
              return (
                <button
                  key={account.id}
                  type="button"
                  onClick={() => toggleAccount(account.id)}
                  className={clsx(
                    'w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 transition-colors',
                    isSelected && 'bg-blue-50'
                  )}
                >
                  {/* Checkbox */}
                  <div
                    className={clsx(
                      'h-4 w-4 rounded border flex items-center justify-center',
                      isSelected
                        ? 'bg-blue-600 border-blue-600'
                        : 'border-gray-300'
                    )}
                  >
                    {isSelected && <Check className="h-3 w-3 text-white" />}
                  </div>

                  {/* Account Info */}
                  <div className="flex-1 text-left">
                    <div className="font-medium text-xs text-gray-900">
                      @{account.username}
                    </div>
                    {account.display_name && (
                      <div className="text-xs text-gray-500">{account.display_name}</div>
                    )}
                  </div>

                  {/* Platform Badge */}
                  <span
                    className={clsx(
                      'px-2 py-0.5 rounded text-xs font-medium',
                      PLATFORM_COLORS[account.platform_id]?.bg || 'bg-gray-100',
                      PLATFORM_COLORS[account.platform_id]?.text || 'text-gray-700'
                    )}
                  >
                    {PLATFORM_NAMES[account.platform_id] || 'Unknown'}
                  </span>
                </button>
              );
            })
          )}
        </div>
      </div>
    </>,
    document.body
  );

  return (
    <div className="space-y-2">
      <label className="block text-xs font-medium text-gray-700">
        Platform Accounts {required && <span className="text-red-500">*</span>}
      </label>

      {/* Dropdown Button */}
      <div className="relative">
        <button
          ref={buttonRef}
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={clsx(
            'w-full flex items-center justify-between px-2 py-1.5 border rounded-lg text-left transition-colors',
            disabled
              ? 'bg-gray-100 cursor-not-allowed'
              : 'bg-white hover:border-gray-400 cursor-pointer',
            error ? 'border-red-500' : 'border-gray-300'
          )}
        >
          <span className="text-gray-500 text-xs">
            {selectedCount === 0
              ? 'Select accounts...'
              : `${selectedCount} account${selectedCount !== 1 ? 's' : ''} selected`}
          </span>
          <ChevronDown
            className={clsx('h-4 w-4 text-gray-400 transition-transform', isOpen && 'rotate-180')}
          />
        </button>
      </div>

      {/* Portal-rendered dropdown */}
      {dropdownContent}

      {/* Error Message */}
      {error && <p className="text-xs text-red-500">{error}</p>}

      {/* Helper Text */}
      {required && selectedAccountIds.length === 0 && !error && (
        <p className="text-xs text-gray-500">At least one account is required</p>
      )}
    </div>
  );
};

export default AccountPicker;
